import { cn } from "@/lib/utils";
import { PreAnnotateModelTypeEnum } from "@repo/schema";
import { useTranslation } from "react-i18next";
import { DEFAULT_MODE_HELP, DEFAULT_MODE_TITLE, MODE_LABEL } from "./copy";
import { SettingsSection } from "./SettingsSection";

interface DefaultModeToggleProps {
  value: PreAnnotateModelTypeEnum;
  onChange: (mode: PreAnnotateModelTypeEnum) => void;
  disabled?: boolean;
  /**
   * Why a single mode cannot be chosen — Segmentation under Robopipe Vision,
   * today. The reason is both the button's tooltip and the section's footer
   * help, so it is readable without hovering a disabled control.
   */
  disabledReasons?: Partial<Record<PreAnnotateModelTypeEnum, string>>;
}

/** Detection leads: it is the default mode, and the only one Vision runs. */
const MODES = [
  PreAnnotateModelTypeEnum.DETECTION,
  PreAnnotateModelTypeEnum.SEGMENTATION,
] as const;

/**
 * Two aria-pressed buttons rather than a radio group: there is no radio-group
 * primitive in modules/shadcn/ui.
 */
export const DefaultModeToggle = ({
  value,
  onChange,
  disabled,
  disabledReasons,
}: DefaultModeToggleProps) => {
  const { t } = useTranslation(["label", "common"]);
  const reason = MODES.map((mode) => disabledReasons?.[mode]).find(Boolean);
  return (
    <SettingsSection
      title={t(DEFAULT_MODE_TITLE)}
      footerHelp={reason ?? t(DEFAULT_MODE_HELP)}
    >
      <div className="grid grid-cols-2 gap-2">
        {MODES.map((mode) => {
          const active = mode === value;
          const modeReason = disabledReasons?.[mode];
          const modeDisabled = disabled || modeReason !== undefined;
          return (
            <button
              key={mode}
              type="button"
              aria-pressed={active}
              disabled={modeDisabled}
              title={modeReason}
              onClick={() => onChange(mode)}
              className={cn(
                "h-11 cursor-pointer rounded-md border text-sm font-medium transition-colors",
                active
                  ? "border-primary bg-primary/10"
                  : "border-border hover:bg-muted",
                modeDisabled &&
                  "cursor-not-allowed opacity-50 hover:bg-transparent",
              )}
            >
              {t(MODE_LABEL[mode])}
            </button>
          );
        })}
      </div>
    </SettingsSection>
  );
};
